import { useContext } from "react";
import { Drawer, Tag, Space, Spin, Empty, Timeline, Tooltip } from "antd";
import {
	CalendarOutlined,
	ClockCircleOutlined,
	EditOutlined,
	EnvironmentOutlined,
	FieldTimeOutlined,
	FlagOutlined,
	HistoryOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	ThunderboltOutlined,
	UserOutlined,
	FileDoneOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import trans, { isRTL, formatDuration } from "../../../utils";
import Button from "../../Helpers/Button/Button";
import { popupcontext } from "../../../context/Popupscontext";
import { useGetSingleRecurringPlannerQuery } from "../../../Redux/service/RecurringPlanners/RecurringPlanners";
import WeekDayChips, { normalizeDays } from "../WeekDayChips";

const BRAND = "#0f6936";

const PLANNER_STATUS_COLORS = {
	ACTIVE: "green",
	PAUSED: "orange",
	CANCELLED: "red",
};

const TASK_STATUS_COLORS = {
	todo: "blue",
	inProgress: "orange",
	done: "green",
	unassigned: "default",
};

const TASK_TIMELINE_COLORS = {
	todo: "blue",
	inProgress: "orange",
	done: "green",
};

// Next date-time the planner will generate a task, or null
const getNextRun = (planner) => {
	if (planner.status !== "ACTIVE") return null;
	const days = normalizeDays(planner.daysOfWeek);
	if (!days.length) return null;
	const [h = 0, m = 0] = (planner.startTime || "00:00")
		.split(":")
		.map(Number);
	const start = dayjs(planner.startDate).startOf("day");
	const end = planner.endDate ? dayjs(planner.endDate).endOf("day") : null;
	let cursor = dayjs().startOf("day");
	if (cursor.isBefore(start)) cursor = start;
	for (let i = 0; i < 370; i++) {
		const day = cursor.add(i, "day");
		if (end && day.isAfter(end)) return null;
		if (days.includes(day.day())) {
			const candidate = day.hour(h).minute(m).second(0);
			if (candidate.isAfter(dayjs())) return candidate;
		}
	}
	return null;
};

const SectionTitle = ({ icon, children }) => (
	<div
		className="d-flex align-items-center gap-2 text-uppercase"
		style={{
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: "0.08em",
			color: "#8a949e",
			margin: "20px 0 10px",
		}}
	>
		{icon}
		{children}
	</div>
);

const InfoRow = ({ icon, label, children }) => (
	<div
		className="d-flex align-items-center"
		style={{ padding: "7px 0", borderBottom: "1px solid #f3f5f4" }}
	>
		<span
			className="d-inline-flex align-items-center justify-content-center"
			style={{
				width: 30,
				height: 30,
				borderRadius: 8,
				backgroundColor: "#f1f6f3",
				color: BRAND,
				fontSize: 14,
				flexShrink: 0,
			}}
		>
			{icon}
		</span>
		<span
			style={{
				width: 110,
				color: "#8a949e",
				fontSize: 13,
				marginInlineStart: 10,
				flexShrink: 0,
			}}
		>
			{label}
		</span>
		<span style={{ fontSize: 14, color: "#1f2937", fontWeight: 500 }}>
			{children}
		</span>
	</div>
);

const StatTile = ({ value, label, color }) => (
	<div
		className="text-center rounded-3"
		style={{
			flex: 1,
			padding: "10px 4px",
			backgroundColor: "#fff",
			border: "1px solid #e9edeb",
		}}
	>
		<div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
		<div
			className="text-capitalize"
			style={{ fontSize: 12, color: "#8a949e" }}
		>
			{label}
		</div>
	</div>
);

export default function ViewPlannerDrawer({
	plannerId,
	onClose,
	canEdit,
	onPauseResume,
}) {
	const { handleShow } = useContext(popupcontext);
	const { data, isFetching } = useGetSingleRecurringPlannerQuery(plannerId, {
		skip: !plannerId,
		refetchOnMountOrArgChange: true,
	});

	const planner = data?.data;
	const nextRun = planner ? getNextRun(planner) : null;
	const tasks = planner?.tasks || [];
	const taskCounts = tasks.reduce(
		(acc, t) => {
			if (t.status === "done") acc.done += 1;
			else if (t.status === "inProgress") acc.inProgress += 1;
			else acc.todo += 1;
			return acc;
		},
		{ done: 0, inProgress: 0, todo: 0 }
	);

	return (
		<Drawer
			open={!!plannerId}
			onClose={onClose}
			placement={isRTL ? "left" : "right"}
			width={520}
			styles={{ body: { padding: 0 } }}
			title={
				planner ? (
					<Space align="center">
						<span className="fw-600">{planner.name}</span>
						<Tag color={PLANNER_STATUS_COLORS[planner.status] || "default"}>
							{trans(planner.status)}
						</Tag>
					</Space>
				) : (
					trans("Planner Details")
				)
			}
			extra={
				planner &&
				canEdit &&
				planner.status !== "CANCELLED" && (
					<Space>
						<Button
							type="default"
							onClick={() => onPauseResume?.(planner)}
							icon={
								planner.status === "ACTIVE" ? (
									<PauseCircleOutlined />
								) : (
									<PlayCircleOutlined />
								)
							}
						>
							{planner.status === "ACTIVE" ? trans("Pause") : trans("Resume")}
						</Button>
						<Button
							type="primary"
							icon={<EditOutlined />}
							onClick={() => {
								onClose();
								handleShow("editForm", "recurringPlanner", planner.id);
							}}
						>
							{trans("Edit")}
						</Button>
					</Space>
				)
			}
		>
			{isFetching || !planner ? (
				<div className="d-flex justify-content-center align-items-center h-100">
					<Spin />
				</div>
			) : (
				<>
					{/* ── Hero: schedule overview ── */}
					<div
						style={{
							background:
								"linear-gradient(160deg, #eef7f1 0%, #f8faf9 60%, #ffffff 100%)",
							borderBottom: "1px solid #e9edeb",
							padding: "20px 24px",
						}}
					>
						<div className="d-flex justify-content-center mb-3">
							<WeekDayChips daysOfWeek={planner.daysOfWeek} size="medium" />
						</div>
						<div
							className="d-flex justify-content-center flex-wrap"
							style={{ gap: 18, fontSize: 13.5, color: "#374151" }}
						>
							<span>
								<ClockCircleOutlined
									style={{ color: BRAND, marginInlineEnd: 6 }}
								/>
								{planner.startTime?.slice(0, 5)} ·{" "}
								{formatDuration(planner.expectedDuration)}
							</span>
							<span>
								<CalendarOutlined
									style={{ color: BRAND, marginInlineEnd: 6 }}
								/>
								{dayjs(planner.startDate).format("DD MMM YYYY")}
								{" → "}
								{planner.endDate
									? dayjs(planner.endDate).format("DD MMM YYYY")
									: trans("Ongoing")}
							</span>
						</div>

						{planner.status === "ACTIVE" && (
							<div className="d-flex justify-content-center mt-3">
								<span
									className="d-inline-flex align-items-center"
									style={{
										gap: 6,
										backgroundColor: "#fff",
										border: `1px solid ${nextRun ? "#bfdccb" : "#e9edeb"}`,
										color: nextRun ? BRAND : "#8a949e",
										borderRadius: 20,
										padding: "5px 14px",
										fontSize: 13,
										fontWeight: 600,
										boxShadow: "0 1px 3px rgba(15,105,54,0.08)",
									}}
								>
									
									{nextRun
										? `${trans("Next run")}: ${nextRun.format(
												"ddd, DD MMM · HH:mm"
										  )}`
										: trans("No upcoming runs")}
								</span>
							</div>
						)}
					</div>

					<div style={{ padding: "4px 24px 24px" }}>
						{/* ── Generated-task stats ── */}
						{tasks.length > 0 && (
							<>
								<SectionTitle icon={<FileDoneOutlined />}>
									{trans("Recently Generated Tasks")}
								</SectionTitle>
								<div className="d-flex" style={{ gap: 10 }}>
									<StatTile
										value={taskCounts.done}
										label={trans("done")}
										color="#16a34a"
									/>
									<StatTile
										value={taskCounts.inProgress}
										label={trans("inProgress")}
										color="#ea8a00"
									/>
									<StatTile
										value={taskCounts.todo}
										label={trans("todo")}
										color="#2563eb"
									/>
								</div>
							</>
						)}

						{/* ── Details ── */}
						<SectionTitle icon={<FieldTimeOutlined />}>
							{trans("Planner Details")}
						</SectionTitle>
						<div>
							<InfoRow icon={<FlagOutlined />} label={trans("Type")}>
								<span className="text-capitalize">{trans(planner.type)}</span>
							</InfoRow>
							<InfoRow
								icon={<ThunderboltOutlined />}
								label={trans("Priority")}
							>
								<Tag
									className="text-capitalize"
									style={{ marginInlineEnd: 0 }}
									color={
										{ low: "blue", medium: "gold", high: "red" }[
											planner.priority
										] || "default"
									}
								>
									{trans(planner.priority)}
								</Tag>
							</InfoRow>
							<InfoRow icon={<HistoryOutlined />} label={trans("Flow")}>
								{planner.isFlexible
									? trans("Flexible Task")
									: trans("Sequential Task")}
							</InfoRow>
							<InfoRow icon={<UserOutlined />} label={trans("Assigned To")}>
								<span className="text-capitalize">
									{planner.assignedTo?.name || trans("Unassigned")}
								</span>
							</InfoRow>
							<InfoRow icon={<EnvironmentOutlined />} label={trans("POI")}>
								{planner.poi?.name || trans("Not assigned")}
							</InfoRow>
						</div>

						{/* ── Linked forms as ordered steps ── */}
						{planner.forms?.length > 0 && (
							<>
								<SectionTitle icon={<FileDoneOutlined />}>
									{trans("Linked Forms")}
								</SectionTitle>
								<div className="d-flex flex-column" style={{ gap: 8 }}>
									{[...planner.forms]
										.sort((a, b) => (a.order || 0) - (b.order || 0))
										.map((f, idx) => (
											<div
												key={f.formId ?? f.form?.id ?? idx}
												className="d-flex align-items-center rounded-3"
												style={{
													gap: 10,
													padding: "8px 12px",
													backgroundColor: "#f8faf9",
													border: "1px solid #e9edeb",
												}}
											>
												<span
													className="d-inline-flex align-items-center justify-content-center"
													style={{
														width: 22,
														height: 22,
														borderRadius: "50%",
														backgroundColor: BRAND,
														color: "#fff",
														fontSize: 11,
														fontWeight: 700,
														flexShrink: 0,
													}}
												>
													{f.order || idx + 1}
												</span>
												<span style={{ fontSize: 13.5, fontWeight: 500 }}>
													{f.form?.name}
												</span>
											</div>
										))}
								</div>
							</>
						)}

						{/* ── Notes ── */}
						{planner.notes && (
							<>
								<SectionTitle icon={<EditOutlined />}>
									{trans("Notes")}
								</SectionTitle>
								<div
									className="rounded-3"
									style={{
										padding: "10px 14px",
										backgroundColor: "#fffbeb",
										border: "1px solid #fde7b3",
										color: "#6b5b2a",
										fontSize: 13.5,
										whiteSpace: "pre-wrap",
									}}
								>
									{planner.notes}
								</div>
							</>
						)}

						{/* ── Recent tasks timeline ── */}
						<SectionTitle icon={<HistoryOutlined />}>
							{trans("Task History")}
						</SectionTitle>
						{tasks.length ? (
							<Timeline
								style={{ marginTop: 6 }}
								items={tasks.map((task) => ({
									color: TASK_TIMELINE_COLORS[task.status] || "gray",
									children: (
										<div className="d-flex justify-content-between align-items-start gap-2">
											<div>
												<div
													style={{
														fontSize: 13.5,
														fontWeight: 600,
														color: "#1f2937",
													}}
												>
													{task.name}
												</div>
												<div style={{ fontSize: 12, color: "#8a949e" }}>
													{task.startTime
														? dayjs(task.startTime).format(
																"ddd, DD MMM YYYY · HH:mm"
														  )
														: "—"}
												</div>
											</div>
											<Tag
												className="text-capitalize"
												style={{ marginInlineEnd: 0 }}
												color={
													TASK_STATUS_COLORS[task.status] ||
													TASK_STATUS_COLORS.unassigned
												}
											>
												{trans(task.status)}
											</Tag>
										</div>
									),
								}))}
							/>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description={trans("No tasks generated yet")}
							/>
						)}

						{/* ── Audit footer ── */}
						<div
							className="d-flex justify-content-between flex-wrap"
							style={{
								marginTop: 20,
								paddingTop: 12,
								borderTop: "1px solid #f0f2f1",
								fontSize: 12,
								color: "#9aa3ae",
								gap: 6,
							}}
						>
							<Tooltip title={dayjs(planner.createdAt).format("DD MMM YYYY HH:mm")}>
								<span className="text-capitalize">
									{trans("Created By")}: {planner.createdBy?.name || "—"} ·{" "}
									{dayjs(planner.createdAt).format("DD MMM YYYY")}
								</span>
							</Tooltip>
							{planner.updatedBy && (
								<Tooltip
									title={dayjs(planner.updatedAt).format("DD MMM YYYY HH:mm")}
								>
									<span className="text-capitalize">
										{trans("last update")}: {planner.updatedBy?.name} ·{" "}
										{dayjs(planner.updatedAt).format("DD MMM YYYY")}
									</span>
								</Tooltip>
							)}
						</div>
					</div>
				</>
			)}
		</Drawer>
	);
}
